import { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { heroSlides } from '@/mocks/home';

export default function HeroSection() {
  const [current, setCurrent] = useState(0);
  const [isTransitioning, setIsTransitioning] = useState(false);

  const goTo = useCallback((index: number) => {
    if (isTransitioning) return;
    setIsTransitioning(true);
    setCurrent(index);
    setTimeout(() => setIsTransitioning(false), 600);
  }, [isTransitioning]);

  const next = useCallback(() => {
    goTo((current + 1) % heroSlides.length);
  }, [current, goTo]);

  useEffect(() => {
    const timer = setInterval(next, 5000);
    return () => clearInterval(timer);
  }, [next]);

  return (
    <section className="relative w-full h-[420px] md:h-[600px] overflow-hidden">
      {heroSlides.map((slide, index) => (
        <div
          key={slide.id}
          className={`absolute inset-0 transition-opacity duration-700 ${
            index === current ? 'opacity-100' : 'opacity-0 pointer-events-none'
          }`}
        >
          <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/25 to-black/50 z-10"></div>
          <img
            src={slide.image}
            alt={slide.title}
            className="w-full h-full object-cover object-top"
          />
        </div>
      ))}

      <div className="absolute inset-0 z-20 flex items-center">
        <div className="w-full px-4 md:px-6">
          <div className="max-w-3xl">
            <span className="inline-block px-3 py-1 rounded-full bg-primary-500/90 text-background-50 text-xs font-medium mb-4">
              {heroSlides[current].category}
            </span>
            <h1 className="text-3xl md:text-5xl lg:text-6xl font-heading font-bold text-background-50 mb-4 leading-tight">
              {heroSlides[current].title}
            </h1>
            <p className="text-base md:text-lg text-background-50/85 mb-6 max-w-xl leading-relaxed">
              {heroSlides[current].description}
            </p>
            <div className="flex items-center gap-4 flex-wrap">
              <Link
                to={`/article/${heroSlides[current].id}`}
                className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-primary-500 text-background-50 font-medium text-sm hover:bg-primary-600 transition-all duration-200 cursor-pointer whitespace-nowrap"
              >
                <div className="w-4 h-4 flex items-center justify-center">
                  <i className="ri-play-fill text-sm"></i>
                </div>
                立即观看
              </Link>
              <Link
                to={`/category/${heroSlides[current].category === '电影' ? 'movie' : heroSlides[current].category === '电视剧' ? 'drama' : 'variety'}`}
                className="inline-flex items-center gap-2 px-6 py-3 rounded-full border border-background-50/30 text-background-50 font-medium text-sm hover:bg-background-50/10 transition-all duration-200 cursor-pointer whitespace-nowrap"
              >
                浏览分类
              </Link>
              <div className="flex items-center gap-2 ml-2">
                <div className="w-5 h-5 flex items-center justify-center">
                  <i className="ri-star-fill text-accent-500 text-sm"></i>
                </div>
                <span className="text-background-50 font-semibold text-sm">{heroSlides[current].rating}</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="absolute bottom-6 left-1/2 -translate-x-1/2 z-20 flex items-center gap-2">
        {heroSlides.map((_, index) => (
          <button
            key={index}
            onClick={() => goTo(index)}
            className={`w-2 h-2 rounded-full transition-all duration-300 cursor-pointer ${
              index === current
                ? 'bg-primary-500 w-6'
                : 'bg-background-50/40 hover:bg-background-50/70'
            }`}
            aria-label={`切换到第${index + 1}张`}
          />
        ))}
      </div>
    </section>
  );
}